import {
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { getColorWithMode } from "../constants/colors";
import { getUserIsLoggedIn, submitNewsletterEmail } from "../utils";
import { isValidEmail } from "../utils/validationUtils";
import { useMediaQuery } from "react-responsive";
import FeaturedGearList from "./FeaturedGearList";
import ExploreGearCard from "./ExploreGearCard";
import ItemImage from "./ItemImage";
import SubNavBar from "./SubNavBar";
import { logUserInteraction } from "../api/logging";
import { Helmet } from "react-helmet";

const HEADING_FONT = '"Inter", "Roboto", "Helvetica", sans-serif';
const IMAGE_BASE = "https://storage.googleapis.com/pack_wizard/static_images";

const getAccent = (darkMode) => (darkMode ? "#90caf9" : "#1976d2");
const getBorder = (darkMode) =>
  darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
const getMutedText = (darkMode) =>
  darkMode ? "rgba(255,255,255,0.68)" : "rgba(0,0,0,0.6)";
const getCardBackground = (darkMode) => (darkMode ? "#333333" : "#FFFFFF");

const RESEARCH_FEATURES = [
  {
    href: "/gear",
    title: "Browse the full catalog",
    text: "Thousands of pieces of gear you can filter & sort by the qualities that matter to you.",
    light: `${IMAGE_BASE}/gear_list_light_1.png`,
    dark: `${IMAGE_BASE}/gear_list_dark_1.png`,
  },
  {
    href: "/gear",
    title: "Live pricing & specs",
    text: "Gear specs, retailers, live pricing & price history — updated every day.",
    light: `${IMAGE_BASE}/gear_light_1.png`,
    dark: `${IMAGE_BASE}/gear_dark_1.png`,
  },
  {
    href: "/gear",
    title: "Compare & read reviews",
    text: "See how gear stacks up against similar gear & read community reviews.",
    light: `${IMAGE_BASE}/gear_insights_light_1.png`,
    dark: `${IMAGE_BASE}/gear_insights_dark_1.png`,
  },
  {
    href: "/gear",
    title: "Deals, refreshed daily",
    text: "The best deals on gear from across the internet, updated every day.",
    light: `${IMAGE_BASE}/deals_light.png`,
    dark: `${IMAGE_BASE}/deals_dark.png`,
  },
];

const GEARLIST_FEATURES = [
  {
    href: "/packs",
    title: "Build lists in minutes",
    text: "Create gear lists lightning fast with autofill from a database of 10,000+ items.",
    light: `${IMAGE_BASE}/autocomplete_light_1.png`,
    dark: `${IMAGE_BASE}/autocomplete_dark_1.png`,
  },
  {
    href: "/packs",
    title: "Share & track your lists",
    text: "Tag your gear or hike, share your list, and monitor your gear list's view count.",
    light: `${IMAGE_BASE}/analytics_light_1.png`,
    dark: `${IMAGE_BASE}/analytics_dark_1.png`,
  },
  {
    href: "/packs",
    title: "Smart gear suggestions",
    text: "Automated suggestions show exactly how much weight you can save.",
    light: `${IMAGE_BASE}/suggest_light_1.png`,
    dark: `${IMAGE_BASE}/suggest_dark_1.png`,
  },
  {
    href: "/browse",
    title: "Get inspired",
    text: "Browse other users' packs — filter by location, season, weight, distance & more.",
    light: `${IMAGE_BASE}/browse_light_1.png`,
    dark: `${IMAGE_BASE}/browse_dark_1.png`,
  },
];

const EXPLORE_CATEGORIES = [
  { link: "/gear/tents", imageUrl: "/xmid2.png", text: "Tents" },
  {
    link: "/gear/sleeping-pads",
    imageUrl: "/helix.png",
    text: "Sleeping Pads",
  },
  { link: "/gear/backpacks", imageUrl: "/reiflash.png", text: "Backpacks" },
  {
    link: "/gear/sleeping-bags",
    imageUrl: "/ee_down.png",
    text: "Sleeping Bags and Quilts",
  },
  {
    link: "/gear/insulated-jackets",
    imageUrl: "/cumulus.png",
    text: "Insulated Jackets",
  },
  { link: "/gear/stoves", imageUrl: "/pocketrocket.png", text: "Stoves" },
];

const SectionHeading = ({ darkMode, eyebrow, title, subtitle }) => (
  <Stack
    alignItems="center"
    gap="10px"
    sx={{ textAlign: "center", marginBottom: "36px", padding: "0px 16px" }}
  >
    {eyebrow && (
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: "12px",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: getAccent(darkMode),
        }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      component="h2"
      sx={{
        fontFamily: HEADING_FONT,
        fontWeight: 800,
        fontSize: "clamp(26px, 4vw, 34px)",
        letterSpacing: "-0.02em",
        lineHeight: 1.15,
        color: getColorWithMode(darkMode, "text"),
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontSize: "15.5px",
          lineHeight: 1.6,
          maxWidth: "640px",
          color: getMutedText(darkMode),
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Stack>
);

const FeatureCard = ({
  darkMode,
  href,
  title,
  text,
  light,
  dark,
  cropAspect,
}) => (
  <Link href={href} underline="none" sx={{ display: "block", height: "100%" }}>
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundColor: getCardBackground(darkMode),
        backgroundImage: "none",
        transition:
          "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          cursor: "pointer",
          boxShadow: darkMode
            ? "0 18px 36px rgba(0,0,0,0.45)"
            : "0 18px 36px rgba(25,118,210,0.14)",
          borderColor: darkMode
            ? "rgba(144,202,249,0.45)"
            : "rgba(25,118,210,0.35)",
        },
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${getBorder(darkMode)}`,
          // cropAspect clips the wrapper to a fixed width:height ratio while
          // the image inside keeps its natural width-driven size — taller
          // screenshots just get their bottom cut off, never rescaled.
          ...(cropAspect
            ? { aspectRatio: String(cropAspect), overflow: "hidden" }
            : {}),
        }}
      >
        <ItemImage
          src={darkMode ? dark : light}
          alt={title}
          style={{ width: "100%", display: "block" }}
          fallbackStyle={{ height: "150px" }}
        />
      </Box>
      <Box sx={{ padding: "16px 18px 20px 18px" }}>
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontWeight: 700,
            fontSize: "15px",
            letterSpacing: "-0.01em",
            marginBottom: "6px",
            color: getColorWithMode(darkMode, "text"),
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: "13.5px",
            lineHeight: 1.6,
            color: getMutedText(darkMode),
          }}
        >
          {text}
        </Typography>
      </Box>
    </Paper>
  </Link>
);

const StatItem = ({ darkMode, value, label }) => (
  <Stack alignItems="center" sx={{ minWidth: "140px" }}>
    <Typography
      sx={{
        fontFamily: HEADING_FONT,
        fontWeight: 800,
        fontSize: "26px",
        letterSpacing: "-0.02em",
        color: getAccent(darkMode),
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: HEADING_FONT,
        fontSize: "13px",
        fontWeight: 500,
        color: getMutedText(darkMode),
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const HomeContainer = ({
  darkMode,
  packName,
  handleSetAuthenticationIsOpen,
}) => {
  const [newsletterEmail, setNewsletterEmail] = React.useState("");
  const [invalidEmail, setInvalidEmail] = React.useState(false);
  const [userDidSubscribe, setUserDidSubscribe] = React.useState(false);

  React.useEffect(() => {
    logUserInteraction("session", "home");
  }, []);

  const handleSubmitNewsletterEmail = () => {
    if (isValidEmail(newsletterEmail)) {
      submitNewsletterEmail(newsletterEmail);
      setUserDidSubscribe(true);
    } else {
      setInvalidEmail(true);
    }
  };

  const isMobile = useMediaQuery({ maxWidth: 898 });
  const isLoggedIn = getUserIsLoggedIn();

  return (
    <>
      <Helmet>
        <title>{packName}</title>
        <meta property="og:title" content={`PackWizard`} />
        <meta property="og:description" content={""} />
        <meta property="og:image" content={`/pack_wizard.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={"PackWizard"} />
        <meta
          name="twitter:description"
          content={
            "PackWizard helps backpackers achieve ultralight status with tools to optimize their gear and receive lighter equipment suggestions."
          }
        />
        <link rel="canonical" href="https://www.packwizard.com" />

        <meta name="twitter:image" content={`/pack_wizard.png`} />
        <meta
          name="keywords"
          content={`${packName ? packName.split(" ").join(",") : ""}`}
        />
        <meta
          name="description"
          content={
            "PackWizard helps backpackers achieve ultralight status with tools to optimize their gear and receive lighter equipment suggestions."
          }
        />
      </Helmet>
      <Container
        sx={{
          width: "100%",
          height: "100%",
          padding: "0px 0px 0px 0px",
          fontFamily: HEADING_FONT,
        }}
      >
        <SubNavBar darkMode={darkMode} />

        {/* Hero */}
        <Box
          sx={{
            background: darkMode
              ? "radial-gradient(ellipse 90% 70% at 50% -10%, rgba(25,118,210,0.22), transparent)"
              : "radial-gradient(ellipse 90% 70% at 50% -10%, rgba(25,118,210,0.09), transparent)",
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems="center"
            gap={6}
            sx={{
              maxWidth: "1180px",
              margin: "0 auto",
              padding: isMobile ? "48px 24px 40px 24px" : "72px 32px 56px 32px",
            }}
          >
            <Stack
              gap="20px"
              alignItems={isMobile ? "center" : "flex-start"}
              sx={{ flex: "1 1 400px", maxWidth: "560px" }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 14px",
                  borderRadius: "999px",
                  border: `1px solid ${getBorder(darkMode)}`,
                  backgroundColor: darkMode
                    ? "rgba(144,202,249,0.08)"
                    : "rgba(25,118,210,0.06)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                    fontSize: "12.5px",
                    color: getAccent(darkMode),
                  }}
                >
                  100% free · No paywalls, ever
                </Typography>
              </Box>
              <Typography
                component="h1"
                textAlign={isMobile ? "center" : "left"}
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 800,
                  fontSize: "clamp(32px, 5vw, 48px)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1.1,
                  color: getColorWithMode(darkMode, "text"),
                }}
              >
                Spend less time planning.{" "}
                <Box
                  component="span"
                  sx={{
                    background: darkMode
                      ? "linear-gradient(90deg, #90caf9, #4fc3f7)"
                      : "linear-gradient(90deg, #1976d2, #42a5f5)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Spend more time outside.
                </Box>
              </Typography>
              <Typography
                textAlign={isMobile ? "center" : "left"}
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: "16.5px",
                  lineHeight: 1.65,
                  color: getMutedText(darkMode),
                }}
              >
                Build gear lists in minutes, not hours. Find your next piece of
                gear instantly. View gear lists for inspiration & more.
              </Typography>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                justifyContent={isMobile ? "center" : "flex-start"}
                sx={{ marginTop: "8px" }}
              >
                {isLoggedIn ? (
                  <Button
                    variant="contained"
                    size="large"
                    href="/packs"
                    sx={{
                      borderRadius: "10px",
                      padding: "10px 28px",
                      fontFamily: HEADING_FONT,
                      fontWeight: 700,
                      textTransform: "none",
                      fontSize: "15px",
                      boxShadow: "0 8px 20px rgba(25,118,210,0.3)",
                    }}
                  >
                    Open My Packs
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleSetAuthenticationIsOpen?.("SIGNUP")}
                      sx={{
                        borderRadius: "10px",
                        padding: "10px 28px",
                        fontFamily: HEADING_FONT,
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "15px",
                        boxShadow: "0 8px 20px rgba(25,118,210,0.3)",
                      }}
                    >
                      Get started — it's free
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => handleSetAuthenticationIsOpen?.("LOGIN")}
                      sx={{
                        borderRadius: "10px",
                        padding: "10px 24px",
                        fontFamily: HEADING_FONT,
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "15px",
                      }}
                    >
                      Log in
                    </Button>
                  </>
                )}
              </Stack>
              {!isLoggedIn && (
                <Link
                  href="/packs"
                  underline="hover"
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontSize: "14px",
                    fontWeight: 500,
                    color: getAccent(darkMode),
                  }}
                >
                  Or try it without an account →
                </Link>
              )}
            </Stack>
            <Link href="/packs" sx={{ flex: "1.2 1 420px", maxWidth: "680px" }}>
              <Box
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1px solid ${getBorder(darkMode)}`,
                  boxShadow: darkMode
                    ? "0 24px 60px rgba(0,0,0,0.5)"
                    : "0 24px 60px rgba(25,118,210,0.18)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    cursor: "pointer",
                    boxShadow: darkMode
                      ? "0 32px 72px rgba(0,0,0,0.6)"
                      : "0 32px 72px rgba(25,118,210,0.26)",
                  },
                }}
              >
                <ItemImage
                  alt="PackWizard gear list builder"
                  style={{
                    width: "100%",
                    display: "block",
                    objectFit: "contain",
                  }}
                  fallbackStyle={{ height: "260px" }}
                  iconSize={48}
                  src={
                    darkMode
                      ? `${IMAGE_BASE}/hero_dark_short_1.png`
                      : `${IMAGE_BASE}/hero_light_short_1.png`
                  }
                />
              </Box>
            </Link>
          </Stack>

          {/* Stats strip */}
          <Stack
            direction="row"
            justifyContent="center"
            flexWrap="wrap"
            gap={isMobile ? 3 : 8}
            sx={{
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "0px 24px 56px 24px",
            }}
          >
            <StatItem
              darkMode={darkMode}
              value="10,000+"
              label="pieces of gear"
            />
            <StatItem darkMode={darkMode} value="Daily" label="price updates" />
            <StatItem darkMode={darkMode} value="Free" label="forever" />
          </Stack>
        </Box>

        {/* Research gear */}
        <Box sx={{ padding: "40px 0px 24px 0px" }}>
          <SectionHeading
            darkMode={darkMode}
            eyebrow="Research"
            title="Research gear & find deals"
            subtitle="Find, compare and research gear across hundreds of brands with live pricing data from your favorite retailers."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "0px 24px",
            }}
          >
            {RESEARCH_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                darkMode={darkMode}
                {...feature}
                cropAspect={1.86}
              />
            ))}
          </Box>
        </Box>

        {/* Create & share */}
        <Box sx={{ padding: "48px 0px 24px 0px" }}>
          <SectionHeading
            darkMode={darkMode}
            eyebrow="Build"
            title="Create & share gear lists"
            subtitle="Everything you need to plan your next trip — from autofill to automated weight-saving suggestions."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "0px 24px",
            }}
          >
            {GEARLIST_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                darkMode={darkMode}
                {...feature}
              />
            ))}
          </Box>
        </Box>

        {/* Featured packs */}
        <Box
          sx={{
            marginTop: "48px",
            padding: "48px 0px",
            backgroundColor: darkMode
              ? "rgba(255,255,255,0.03)"
              : "rgba(25,118,210,0.03)",
            borderTop: `1px solid ${getBorder(darkMode)}`,
            borderBottom: `1px solid ${getBorder(darkMode)}`,
          }}
        >
          <SectionHeading
            darkMode={darkMode}
            eyebrow="Community"
            title="Featured packs"
          />
          <FeaturedGearList isDarkMode={darkMode} />
        </Box>

        {/* Explore categories */}
        <Box sx={{ padding: "56px 0px 24px 0px" }}>
          <SectionHeading
            darkMode={darkMode}
            eyebrow="Explore"
            title="Find your next piece of gear"
            subtitle="Quickly compare specs and features with live pricing data across hundreds of brands."
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            gap={2}
            sx={{ maxWidth: "1180px", margin: "0 auto", padding: "0px 24px" }}
          >
            {EXPLORE_CATEGORIES.map((category) => (
              <ExploreGearCard key={category.link} {...category} />
            ))}
          </Stack>
        </Box>

        {/* Newsletter */}
        <Box sx={{ padding: "48px 24px 72px 24px" }}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: "720px",
              margin: "0 auto",
              padding: isMobile ? "32px 24px" : "44px 48px",
              borderRadius: "20px",
              textAlign: "center",
              border: `1px solid ${getBorder(darkMode)}`,
              backgroundColor: getCardBackground(darkMode),
              backgroundImage: "none",
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontFamily: HEADING_FONT,
                fontWeight: 800,
                fontSize: "clamp(22px, 3vw, 28px)",
                letterSpacing: "-0.02em",
                marginBottom: "10px",
                color: getColorWithMode(darkMode, "text"),
              }}
            >
              Stay in the loop
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: "15px",
                lineHeight: 1.6,
                maxWidth: "520px",
                margin: "0 auto 24px auto",
                color: getMutedText(darkMode),
              }}
            >
              Join our weekly newsletter to find out when we add new gear, and
              get updates about deals and sales on all of your favorite gear.
            </Typography>
            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              gap={1.5}
            >
              <TextField
                size="small"
                sx={{
                  width: "min(340px, 100%)",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontFamily: HEADING_FONT,
                  },
                }}
                placeholder="Email address"
                error={invalidEmail}
                helperText={invalidEmail ? "Please enter a valid email" : ""}
                onChange={(e) => {
                  setInvalidEmail(false);
                  setNewsletterEmail(e.target.value);
                  setUserDidSubscribe(false);
                }}
              />
              {userDidSubscribe ? (
                <Button
                  disabled
                  variant="contained"
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                  }}
                >
                  Subscribed ✓
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmitNewsletterEmail}
                  sx={{
                    borderRadius: "10px",
                    padding: "8px 24px",
                    textTransform: "none",
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                  }}
                >
                  Subscribe
                </Button>
              )}
            </Stack>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default HomeContainer;
